/**
 * Console utilities
 *
 * Patches to the global `console` object that adjust log routing for
 * third-party libraries whose verbosity we can't control through their
 * own configuration APIs.
 *
 * Call `applyConsolePatches()` once at app startup (main.ts) before any
 * libraries are initialized, so the patches are in place before logging begins.
 */

/**
 * Amazon Connect Streams legacy logger format.
 *
 * The Streams SDK ships two loggers:
 *
 *   1. Legacy `connect.rootLogger` (what `connect.getLog()` returns).
 *      This logger writes every entry through a `CONSOLE_LOGGER_MAP` that
 *      routes TRACE, DEBUG, and INFO all to `console.info`, and WARN/ERROR to
 *      `console.warn`/`console.error`.  Its default echo level is INFO, so all
 *      INFO-and-above entries appear in the browser console.
 *
 *   2. Newer `@amazon-connect/core` ConnectLogger instances used internally by
 *      more recent parts of the SDK.  These default to echoing only ERROR-level
 *      entries and are not a concern.
 *
 * The legacy logger formats each entry as:
 *
 *   [ISO8601_TIMESTAMP] [LEVEL] [AGENT_RESOURCE_ID]: message text
 *
 * e.g.:
 *   [2024-06-01T14:32:05.123Z] [INFO] [a1b2c3d4]: CCP initialized
 *
 * There is no public API to reroute individual log levels to a different
 * console method — `logConfig.echoLevel` in `initCCP` can only raise the
 * threshold, which would suppress INFO entirely rather than demote it.
 *
 * This regex matches the prefix of any legacy Streams log entry whose level
 * is TRACE, DEBUG, or INFO (all three of which the SDK routes to console.info).
 */
const CONNECT_STREAMS_LOG_RE =
  /^\[\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z\] \[(TRACE|DEBUG|INFO)\] \[/

/**
 * Demotes Amazon Connect Streams INFO/DEBUG/TRACE logs from `console.info`
 * to `console.debug`.
 *
 * Browser devtools hide `console.debug` output by default (requires "Verbose"
 * level to be enabled), so this keeps the Connect log noise out of the console
 * during normal development without losing the entries entirely.
 *
 * WARN and ERROR entries are unaffected — the Streams SDK routes those through
 * `console.warn` and `console.error` directly, bypassing `console.info`.
 */
function demoteConnectInfoLogs(): void {
  const originalInfo = console.info.bind(console)
  console.info = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && CONNECT_STREAMS_LOG_RE.test(args[0])) {
      console.debug(...args)
    } else {
      originalInfo(...args)
    }
  }
}

/**
 * Apply all console patches.
 *
 * Call this once at app startup before any libraries are initialized.
 */
export function applyConsolePatches(): void {
  demoteConnectInfoLogs()
}
